import styles from './NumericStepper.module.scss';

// Minus icon (14x14)
const MinusIcon = ({ disabled = false }: { disabled?: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2.91667 7.58333V6.41667H11.0833V7.58333H2.91667Z"
      fill={disabled ? '#A5A5A5' : 'currentColor'}
    />
  </svg>
);

// Plus icon (14x14)
const PlusIcon = ({ disabled = false }: { disabled?: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.41667 7.58333H2.91667V6.41667H6.41667V2.91667H7.58333V6.41667H11.0833V7.58333H7.58333V11.0833H6.41667V7.58333Z"
      fill={disabled ? '#A5A5A5' : 'currentColor'}
    />
  </svg>
);

export interface NumericStepperProps {
  /** Current value */
  value: number;

  /** Called when value changes */
  onChange: (value: number) => void;

  /** Minimum value (default: 0) */
  min?: number;

  /** Maximum value (default: 100) */
  max?: number;

  /** Step increment (default: 1) */
  step?: number;

  /** Disabled state */
  disabled?: boolean;

  /** Additional CSS classes */
  className?: string;
}

export const NumericStepper = ({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  className = '',
}: NumericStepperProps) => {
  const isAtMin = value <= min;
  const isAtMax = value >= max;

  const handleDecrement = () => {
    if (!disabled && !isAtMin) {
      const newValue = Math.max(min, value - step);
      onChange(newValue);
    }
  };

  const handleIncrement = () => {
    if (!disabled && !isAtMax) {
      const newValue = Math.min(max, value + step);
      onChange(newValue);
    }
  };

  const containerClassName = [styles.container, disabled ? styles.disabled : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={containerClassName}>
      <button
        type="button"
        className={`${styles.button} ${isAtMin || disabled ? styles.buttonDisabled : ''}`}
        onClick={handleDecrement}
        disabled={disabled || isAtMin}
        aria-label="Decrease value"
      >
        <MinusIcon disabled={isAtMin || disabled} />
      </button>

      <div className={styles.value}>
        <span>{value}</span>
      </div>

      <button
        type="button"
        className={`${styles.button} ${isAtMax || disabled ? styles.buttonDisabled : ''}`}
        onClick={handleIncrement}
        disabled={disabled || isAtMax}
        aria-label="Increase value"
      >
        <PlusIcon disabled={isAtMax || disabled} />
      </button>
    </div>
  );
};
